import { useMemo, useState } from "react";
import { MapLayerMouseEvent } from "react-map-gl/maplibre";

import { combineExtents } from "@nens/shared-utils";

import { getExtentLayerIds } from "@/lib/rana-map/components/rana-map-layers";
import { EXTENT } from "@/utils/schematisation-extents";

import { SchematisationMapItem } from "../types/schematisation-map-item";

type UseSchematisationsMapProps = {
  schematisationItemsOnMap: SchematisationMapItem[];
};

export const SCHEMATISATIONS_EXTENTS_ID = "schematisation-extents";
const { fillId: SCHEMATISATIONS_EXTENTS_FILL_LAYER_ID } = getExtentLayerIds(
  SCHEMATISATIONS_EXTENTS_ID
);

type PopupState = {
  point: [number, number];
  items: SchematisationMapItem[];
};

export function useSchematisationsMap({ schematisationItemsOnMap }: UseSchematisationsMapProps) {
  const [popup, setPopup] = useState<PopupState | null>(null);

  // The map hook only turns prepared map items into map bounds, layers, and click state.
  const mapBounds = useMemo(
    () => combineExtents(schematisationItemsOnMap.map(({ bounds }) => bounds)) ?? EXTENT,
    [schematisationItemsOnMap]
  );

  const extentBounds = useMemo(
    () => schematisationItemsOnMap.map(({ bounds }) => bounds),
    [schematisationItemsOnMap]
  );

  const clickableLayerIds = useMemo(() => [SCHEMATISATIONS_EXTENTS_FILL_LAYER_ID], []);

  const onMapClick = (event: MapLayerMouseEvent) => {
    // Find every model whose extent contains the clicked point so the popup can show all matches.
    const clickPoint: [number, number] = [event.lngLat.lng, event.lngLat.lat];
    const [clickLng, clickLat] = clickPoint;

    const clickedItems = schematisationItemsOnMap.filter(
      ({ bounds: [minLng, minLat, maxLng, maxLat] }) => {
        return clickLng >= minLng && clickLng <= maxLng && clickLat >= minLat && clickLat <= maxLat;
      }
    );

    if (clickedItems.length === 0) {
      setPopup(null);
      return;
    }

    setPopup({
      point: clickPoint,
      items: clickedItems,
    });
  };

  const hasItemsOnMap = schematisationItemsOnMap.length > 0;

  return {
    popup,
    setPopup,
    onMapClick,
    mapBounds,
    extentBounds,
    clickableLayerIds,
    schematisationItemsOnMap,
    hasItemsOnMap,
  };
}
